import React, { useState, useEffect } from 'react';
import { Menu, X, Instagram, Facebook } from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (path) => {
    setMobileOpen(false);
    if (location.pathname !== path) {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  const linkCls = 'text-sm font-medium text-slate-700 hover:text-brand-primary transition-colors cursor-pointer';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-lg shadow-sm border-b border-slate-200/60' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            onClick={() => handleNav('/')}
            className="flex items-center group"
          >
            <img src="/logo.png" alt="Royal Dental Assistant Academy" className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105" />
          </button>

          <nav className="hidden md:flex items-center gap-8">
            <a className={linkCls} onClick={() => handleNav('/about')}>{t.nav.about}</a>
            <a className={linkCls} onClick={() => handleNav('/program')}>{t.nav.programs}</a>
            <a className={linkCls} onClick={() => handleNav('/tuition')}>{t.nav.tuition}</a>
            <a className={linkCls} onClick={() => handleNav('/schedule')}>{t.nav.schedule}</a>
            <a className={linkCls} onClick={() => handleNav('/faq')}>{t.nav.faq}</a>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden lg:flex items-center gap-3 mr-2 border-r border-slate-200 pr-5">
              <a href="https://www.instagram.com/royaldaacademy/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-pink-500 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/share/1AcAVf8mbm/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.tiktok.com/@royaldaacademy" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.01.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.12-3.44-3.17-3.64-5.41-.05-.56-.04-1.11.02-1.66.19-1.57.85-3.04 1.86-4.21 1.34-1.51 3.3-2.4 5.31-2.47.01 1.34.01 2.68 0 4.02-.91-.01-1.8.27-2.5.83-1.07.82-1.66 2.21-1.43 3.55.15.86.61 1.62 1.25 2.16.8.69 1.86 1.02 2.9 1.01 1.6-.01 2.96-.92 3.56-2.39.29-.68.42-1.43.43-2.18.04-4.89.02-9.78.02-14.67z"/>
                </svg>
              </a>
            </div>

            <button
              onClick={toggle}
              className="inline-flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-full border border-slate-200 bg-white/60 hover:bg-white text-[10px] md:text-xs font-semibold text-slate-700 hover:border-brand-primary transition-all mr-1 md:mr-0"
              aria-label="Toggle language"
            >
              <span className={lang === 'en' ? 'text-brand-primary' : 'text-slate-400'}>EN</span>
              <span className="text-slate-300">/</span>
              <span className={lang === 'es' ? 'text-brand-primary' : 'text-slate-400'}>ES</span>
            </button>
            <Button
              onClick={() => handleNav('/application')}
              className="hidden md:inline-flex bg-brand-primary hover:from-blue-800 hover:to-blue-900 text-white shadow-md shadow-blue-900/20 rounded-full px-5"
            >
              {t.nav.apply}
            </Button>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100"
              aria-label="Open menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md">
          <div className="px-4 py-4 flex flex-col gap-1">
            <a
              onClick={() => handleNav('/about')}
              className="block px-4 py-3 rounded-xl text-slate-800 font-semibold hover:bg-slate-50"
            >
              {t.nav.about}
            </a>
            <a
              onClick={() => handleNav('/program')}
              className="block px-4 py-3 rounded-xl text-slate-800 font-semibold hover:bg-slate-50"
            >
              {t.nav.programs}
            </a>
            <a
              onClick={() => handleNav('/tuition')}
              className="block px-4 py-3 rounded-xl text-slate-800 font-semibold hover:bg-slate-50"
            >
              {t.nav.tuition}
            </a>
            <a
              onClick={() => handleNav('/schedule')}
              className="block px-4 py-3 rounded-xl text-slate-800 font-semibold hover:bg-slate-50"
            >
              {t.nav.schedule}
            </a>
            <a
              onClick={() => handleNav('/faq')}
              className="block px-4 py-3 rounded-xl text-slate-800 font-semibold hover:bg-slate-50"
            >
              {t.nav.faq}
            </a>
            <div className="pt-2">
              <Button
                onClick={() => handleNav('/application')}
                className="w-full bg-brand-primary text-white rounded-xl h-12 text-base font-bold shadow-md shadow-blue-900/20"
              >
                {t.nav.apply}
              </Button>
            </div>
            <div className="flex justify-center items-center gap-6 pt-6 pb-2 border-t border-slate-100 mt-2">
              <a href="https://www.instagram.com/royaldaacademy/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-pink-500 transition-colors">
                <Instagram className="w-6 h-6" />
              </a>
              <a href="https://www.facebook.com/share/1AcAVf8mbm/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600 transition-colors">
                <Facebook className="w-6 h-6" />
              </a>
              <a href="https://www.tiktok.com/@royaldaacademy" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.01.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.12-3.44-3.17-3.64-5.41-.05-.56-.04-1.11.02-1.66.19-1.57.85-3.04 1.86-4.21 1.34-1.51 3.3-2.4 5.31-2.47.01 1.34.01 2.68 0 4.02-.91-.01-1.8.27-2.5.83-1.07.82-1.66 2.21-1.43 3.55.15.86.61 1.62 1.25 2.16.8.69 1.86 1.02 2.9 1.01 1.6-.01 2.96-.92 3.56-2.39.29-.68.42-1.43.43-2.18.04-4.89.02-9.78.02-14.67z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
